import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useDashLayoutCtx } from "../DashLayoutCtxProvider";
import { Box, Typography } from "@mui/material";
import useNetworkRequest from "../../../../hooks/NetworkRequest";
import { ENDPOINTS, urlWithPrefix } from "../../../../configs/APIConfig";
import APP_CONSTANTS from "../../../../constants/AppConstants";
import notify from "../../../../funcs/ToastFuncs";
import NumberPlate from "../../../common/number-plate/NumberPlate";
import COLORS from "../../../../constants/Colors";
import DetailLoading from "../detail-loading/DetailLoading";
// import WheelzBadge from "../../../../assets/images/wheelz_badge.png";
import TruckImage from "../../../../assets/images/truck_image.png";
import InfoGrid from "../../../common/info-grid/InfoGrid";
import TruckDetailError from "./TruckDetailError";
import { useNavigate } from "react-router-dom";
import TrackingAlertBanner from "../track-alert-banner/TrackingAlertBanner";

const TruckDetail = ({ vehRegNo, setRegNo }) => {
  const { setsplitView, saveTruck, isTruckSaved, settransferBin } = useDashLayoutCtx();
  const closeDetailView = () => {
    setsplitView(false);
    setRegNo("");
    settransferBin(null);
  };
  const { fetchData } = useNetworkRequest();
  const abortControllerRef = useRef(null);
  const navigate = useNavigate();
  const navToErrorScreen = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    navigate("/error", {
      state: {
        releaseProtection: true,
      },
    });
  };
  // const [isFetchingvehDetail, setisFetchingvehDetail] = useState(null);
  // const [vehDetail, setvehDetail] = useState(null);

  const closeFuncForLoading = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    closeDetailView();
  };

  const [renderComponent, setrenderComponent] = useState(<></>);

  useEffect(() => {
    if (vehRegNo) {
      if (APP_CONSTANTS.VEHICLE_REG_NO_REGEX.test(vehRegNo)) {
        getVehicleDetails();
      } else {
        notify("Invalid vehicle number", "error");
      }
    }
    // eslint-disable-next-line
  }, [vehRegNo]);

  const getVehicleDetails = async () => {
    setrenderComponent(
      <DetailLoading closeFunc={closeFuncForLoading} vehRegNo={vehRegNo} />
    );
    // setisFetchingvehDetail(true);
    // setvehDetail(null);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const payload = {
      vehicle_number: vehRegNo,
    };

    const timeoutPromise = new Promise((resolve) =>
      setTimeout(resolve, APP_CONSTANTS.TRUCK_DETAIL_LOADING_MIN_DELAY)
    );

    try {
      const fetchPromise = fetchData({
        url: urlWithPrefix(ENDPOINTS.DASHBOARD.TRUCKS.GET_DETAIL.url),
        method: ENDPOINTS.DASHBOARD.TRUCKS.GET_DETAIL.method,
        authRequired: true,
        timeout: 20000, // 20 Sec timeout
        signal: abortController.signal,
        body: payload,
      });

      const [resp] = await Promise.all([fetchPromise, timeoutPromise]);

      const detail = resp.data;
      // setvehDetail(detail);
      if (detail?.data?.rc_no === vehRegNo) {
        setrenderComponent(
          <DetailView
            detail={detail?.data}
            trackingEnabled={detail?.tracking_status === "enabled" || false}
          />
        );
      } else {
        throw new Error(
          `Failed to fetch vehicle details. Status code: ${resp.status}`
        );
      }
    } catch (error) {
      const [e] = await Promise.all([error, timeoutPromise]);
      console.error("Vehicle Details Error", e);

      if (error.response.status === 400) {
        if (error.response.data.error.code === "invalid-vehicle-category") {
          setrenderComponent(
            <TruckDetailError
              closeFunc={closeDetailView}
              vehRegNo={vehRegNo}
              invalidVehicle
            />
          );
        } else {
          setrenderComponent(
            <TruckDetailError
              closeFunc={closeDetailView}
              vehRegNo={vehRegNo}
              failedFetch
            />
          );
        }
      } else {
        // if not a 400 error, navigate to error screen
        navToErrorScreen();
      }
    }
    // setisFetchingvehDetail(false);
  };

  const DetailView = ({ detail, trackingEnabled }) => {
    const [haveSaved, sethaveSaved] = useState(false);
    let registrationData = [
      { label: "RC Number", value: detail?.rc_no },
      { label: "RC Status", value: detail?.rc_status },
      { label: "Registered At", value: detail?.registered_at },
      { label: "Registered On", value: detail?.regn_date },
      { label: "Owner Name", value: detail?.owner_name },
      { label: "Blacklist Status", value: detail?.blacklist_status },
    ];
    let vehicleData = [
      { label: "Category", value: detail?.category },
      { label: "Category Desc.", value: detail?.category_description },
      { label: "Manufacturer", value: detail?.maker_desc },
      { label: "Body Type", value: detail?.body_type },
      { label: "Fuel Type", value: detail?.fuel_type },
      { label: "Norms Type", value: detail?.norms_type },
      { label: "Manufactured On", value: detail?.mfg_month_year },
    ];
    let fitnessData = [
      { label: "Fitness Upto", value: detail?.fitness_upto },
      { label: "Tax Upto", value: detail?.tax_upto },
      { label: "Pucc No", value: detail?.pucc_no },
      { label: "Pucc Upto", value: detail?.pucc_upto },
    ];
    let insuranceData = [
      { label: "Insurance Company", value: detail?.insurance_comp },
      { label: "Insurance No", value: detail?.insurance_no },
      { label: "Insurance Upto", value: detail?.insurance_upto },
      { label: "financier", value: detail?.financier },
    ];
    let permitData = [
      { label: "Permit Type", value: detail?.permit_type },
      { label: "Permit No", value: detail?.permit_no },
      { label: "Permit Issued On", value: detail?.permit_issue_dt },
      { label: "Permit Valid From", value: detail?.permit_valid_from },
      { label: "State Permit Valid Till", value: detail?.permit_valid_till },
      { label: "National Permit Valid Till", value: detail?.national_permit_valid_upto },
    ];
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "start",
          width: "100%",
        }}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          width={"calc(100% - 40px)"}
          sx={{
            opacity: "0.8",
            padding: "20px",
          }}
        >
          {!(isTruckSaved(detail.rc_no) || haveSaved) && (
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={COLORS.PRIMARY}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => {
                sethaveSaved(true);
                saveTruck(detail.rc_no);
              }}
            >
              SAVE
            </Typography>
          )}
          {(isTruckSaved(detail.rc_no) || haveSaved) && (
            <Typography
              fontSize={"0.8rem"}
              fontWeight={600}
              color={COLORS.PRIMARY}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => {
                notify("Truck already saved.", "success");
              }}
            >
              SAVED
            </Typography>
          )}
          <Typography fontSize={"0.8rem"} fontWeight={600}>
            {detail?.maker_desc}
          </Typography>
          <Typography
            fontSize={"0.8rem"}
            fontWeight={600}
            color={COLORS.PRIMARY}
            sx={{
              cursor: "pointer",
            }}
            onClick={closeDetailView}
          >
            CLOSE
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                height: "120px",
                width: "180px",
                mb: "10px",
                backgroundImage: `url(${TruckImage})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          </Box>
          <NumberPlate regNo={detail?.rc_no} />
          {/* <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "end",
            }}
          >
            <Box
              sx={{
                height: "31px",
                width: "88px",
                mt: "10px",
                backgroundImage: `url(${WheelzBadge})`,
                backgroundSize: "contain",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "center",
              }}
            />
          </Box> */}
        </Box>
        <TrackingAlertBanner
          vehRegNo={detail?.rc_no}
          isEnabled={trackingEnabled}
          ownerName={detail?.owner_name}
          manufName={detail?.maker_desc}
        />
        <InfoGrid title="Registration" data={registrationData} />
        <InfoGrid title="Vehicle Details" data={vehicleData} />
        <InfoGrid title="FITNESS & COMPLIANCE" data={fitnessData} />
        <InfoGrid title="INSURANCE & FINANCIER" data={insuranceData} />
        <InfoGrid title="Permit" data={permitData} />
      </Box>
    );
  };

  return renderComponent;
};

TruckDetail.propTypes = {
  vehRegNo: PropTypes.string.isRequired,
  setRegNo: PropTypes.func.isRequired,
};

export default TruckDetail;
